import { useState, useCallback, useMemo } from 'react';

/**
 * useWizardNavigation
 *
 * Abstract navigation state management for multi-step wizards.
 * Separates navigation logic from UI and domain-specific state.
 *
 * @param {number} totalSteps - Total number of steps in the wizard
 * @param {object} options - Configuration options
 * @param {number} options.initialStep - Starting step (default: 1)
 * @param {function} options.onStepChange - Callback when step changes
 *
 * @returns {object} Navigation state and controls
 */
export const useWizardNavigation = (totalSteps, options = {}) => {
  const {
    initialStep = 1,
    onStepChange
  } = options;

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [canAdvance, setCanAdvance] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Navigate to next step
  const goToNext = useCallback(() => {
    if (canAdvance && currentStep < totalSteps && !isLoading) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setCanAdvance(false); // Reset validation for next step

      if (onStepChange) {
        onStepChange(nextStep);
      }

      // Scroll to top when step changes
      window.scrollTo(0, 0);
    }
  }, [canAdvance, currentStep, totalSteps, isLoading, onStepChange]);

  // Navigate to previous step
  const goToPrevious = useCallback(() => {
    if (currentStep > 1 && !isLoading) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setCanAdvance(true); // Previous steps are always valid

      if (onStepChange) {
        onStepChange(prevStep);
      }

      window.scrollTo(0, 0);
    }
  }, [currentStep, isLoading, onStepChange]);

  // Jump to specific step (useful for non-linear navigation)
  const goToStep = useCallback((stepNumber) => {
    if (stepNumber >= 1 && stepNumber <= totalSteps && !isLoading) {
      setCurrentStep(stepNumber);

      if (onStepChange) {
        onStepChange(stepNumber);
      }

      window.scrollTo(0, 0);
    }
  }, [totalSteps, isLoading, onStepChange]);

  // Button state calculations
  const buttonStates = useMemo(() => ({
    // Navigation capabilities
    canGoNext: canAdvance && currentStep < totalSteps && !isLoading,
    canGoPrevious: currentStep > 1 && !isLoading,
    canFinish: currentStep === totalSteps && !isLoading,

    // Visibility flags
    showNext: currentStep < totalSteps,
    showPrevious: currentStep > 1,
    showFinish: currentStep === totalSteps,

    // State flags
    isFirstStep: currentStep === 1,
    isLastStep: currentStep === totalSteps,
    isLoading
  }), [canAdvance, currentStep, totalSteps, isLoading]);

  // Progress calculation
  const progress = useMemo(() => ({
    current: currentStep,
    total: totalSteps,
    percentage: Math.round((currentStep / totalSteps) * 100),
    stepsCompleted: currentStep - 1,
    stepsRemaining: totalSteps - currentStep
  }), [currentStep, totalSteps]);

  return {
    // Current state
    currentStep,
    totalSteps,
    progress,

    // Navigation methods
    goToNext,
    goToPrevious,
    goToStep,

    // State setters (for step components)
    setCanAdvance,
    setIsLoading,

    // Computed button states
    buttonStates
  };
};
